<template>
  <div id="stickyBar">
    <v-app-bar 
      class="v-app-bar-fixed-mobile" 
      :color="NOTARIA.Theme.DatosContacto.StickyBarCSS.BackgroundColor" 
      height="auto"
      min-height="30"
      :absolute="!NOTARIA.Theme.DatosContacto.Fixed"
    >
      <v-container class="px-4 px-sm-6 py-0 ma-0 fill-height" fluid>
        <div class="d-flex justify-space-between align-center flex-grow-1 stickybar-text" style="height: 100%;">
          
          <!-- Left Side: Phone & Address -->
          <div class="d-flex align-center text-truncate">
            <!-- Telefono -->
            <a v-if="NOTARIA.Contacto.Telefono && NOTARIA.Theme.DatosContacto.MostrarTelefono" 
               :href="'tel:' + NOTARIA.Contacto.Telefono"
               :title="'Marcar telefono de la notaría: ' + NOTARIA.Contacto.Telefono"
               class="text-decoration-none d-flex align-center text-truncate mr-4"
               :style="{
                 color: NOTARIA.Theme.DatosContacto.Letra.Color,
                 fontFamily: NOTARIA.Theme.DatosContacto.Letra.Font,
                 fontSize: NOTARIA.Theme.DatosContacto.Letra.Size,
                 fontWeight: NOTARIA.Theme.DatosContacto.Letra.Weight
               }">
              <v-icon class="mr-1" small :color="NOTARIA.Theme.DatosContacto.Letra.Color">mdi-phone</v-icon>
              <span>{{ NOTARIA.Contacto.Telefono }}</span>
            </a>

            <!-- Separador -->
            <span class="d-none d-md-inline mr-4" 
               v-if="(NOTARIA.Contacto.Telefono && NOTARIA.Theme.DatosContacto.MostrarTelefono) && (NOTARIA.Contacto.Direccion && NOTARIA.Theme.DatosContacto.MostrarDireccion)"
               :style="{ color: NOTARIA.Theme.DatosContacto.Letra.Color }">|</span>

            <!-- Direccion -->
            <a v-if="NOTARIA.Contacto.Direccion && NOTARIA.Theme.DatosContacto.MostrarDireccion" 
               :href="NOTARIA.Contacto.EnlaceMaps" 
               title="Abrir dirección de la notaría en google maps" 
               target="_blank"
               class="text-decoration-none d-none d-md-flex align-center text-truncate"
               :style="{
                 color: NOTARIA.Theme.DatosContacto.Letra.Color,
                 fontFamily: NOTARIA.Theme.DatosContacto.Letra.Font,
                 fontSize: NOTARIA.Theme.DatosContacto.Letra.Size,
                 fontWeight: NOTARIA.Theme.DatosContacto.Letra.Weight
               }">
              <v-icon class="mr-1" small :color="NOTARIA.Theme.DatosContacto.Letra.Color">mdi-map-marker</v-icon>
              <span class="text-truncate" style="max-width: 500px;">
                {{ NOTARIA.Contacto.Direccion }} {{ NOTARIA.Contacto.CP }}, {{ NOTARIA.Contacto.Municipio }}, {{ NOTARIA.Contacto.Provincia }}
              </span>
            </a>
          </div>

          <!-- Centro: aviso del encargo online, ref: https://trello.com/c/H8zH4fAK
               En móvil no sale si ya está el botón flotante, para no repetir la llamada -->
          <div v-if="mostrarAviso" class="aviso-encargo align-center text-truncate px-2"
               :class="acceso.flotante.value ? 'd-none d-md-flex' : 'd-flex'">
            <NuxtLink :to="acceso.enlace('barra')" class="d-flex align-center text-truncate" :style="estiloAviso">
              <v-icon class="mr-1" small :color="NOTARIA.Theme.DatosContacto.Letra.Color">mdi-file-document-edit-outline</v-icon>
              <span class="text-truncate">{{ textoAviso }}</span>
              <v-icon class="ml-1" small :color="NOTARIA.Theme.DatosContacto.Letra.Color">mdi-arrow-right</v-icon>
            </NuxtLink>
            <button type="button" class="aviso-cerrar ml-2" :aria-label="t('Cerrar aviso')" :title="t('Cerrar aviso')" @click="cerrarAviso">
              <v-icon small :color="NOTARIA.Theme.DatosContacto.Letra.Color">mdi-close</v-icon>
            </button>
          </div>

          <!-- Right Side: Email -->
          <div class="d-flex align-center justify-end text-truncate pl-4"
               v-if="(NOTARIA.Contacto.Email && NOTARIA.Theme.DatosContacto.MostrarEmail) || (NOTARIA.Contacto.Email2 && NOTARIA.Theme.DatosContacto.MostrarEmail2)">
            <v-icon class="mr-1" small :color="NOTARIA.Theme.DatosContacto.Letra.Color">mdi-email</v-icon>
            <a v-if="NOTARIA.Contacto.Email && NOTARIA.Theme.DatosContacto.MostrarEmail" 
               :href="'mailto:' + NOTARIA.Contacto.Email"
               :title="'Enviar correo electronico a la dirección de la notaría: ' + NOTARIA.Contacto.Email" 
               class="text-decoration-none d-flex align-center text-truncate"
               :style="{
                 color: NOTARIA.Theme.DatosContacto.Letra.Color,
                 fontFamily: NOTARIA.Theme.DatosContacto.Letra.Font,
                 fontSize: NOTARIA.Theme.DatosContacto.Letra.Size,
                 fontWeight: NOTARIA.Theme.DatosContacto.Letra.Weight
               }">
               <span>{{ NOTARIA.Contacto.Email }}</span>
            </a>
            <span class="mx-2 d-none d-md-inline" 
                v-if="(NOTARIA.Contacto.Email && NOTARIA.Theme.DatosContacto.MostrarEmail) && (NOTARIA.Contacto.Email2 && NOTARIA.Theme.DatosContacto.MostrarEmail2)"
                :style="{ color: NOTARIA.Theme.DatosContacto.Letra.Color }">|</span>
            <a v-if="NOTARIA.Contacto.Email2 && NOTARIA.Theme.DatosContacto.MostrarEmail2" 
               :href="'mailto:' + NOTARIA.Contacto.Email2"
               :title="'Enviar correo electronico a la dirección de la notaría: ' + NOTARIA.Contacto.Email2" 
               class="text-decoration-none d-none d-md-flex align-center text-truncate"
               :style="{
                 color: NOTARIA.Theme.DatosContacto.Letra.Color,
                 fontFamily: NOTARIA.Theme.DatosContacto.Letra.Font,
                 fontSize: NOTARIA.Theme.DatosContacto.Letra.Size,
                 fontWeight: NOTARIA.Theme.DatosContacto.Letra.Weight
               }">
               <span>{{ NOTARIA.Contacto.Email2 }}</span>
            </a>
          </div>

        </div>
      </v-container>
    </v-app-bar>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useStore } from '../store'
import { useAccesoSolicitud, PAGINAS_ENCARGO } from '../composables/solicitudAcceso'
import { t } from '../composables/solicitudTextos'
const store = useStore();
const { NOTARIA } = storeToRefs(store)

// Aviso del encargo online: se cierra para el resto de la visita (sessionStorage)
const CLAVE_AVISO_CERRADO = 'avisoEncargoBarraCerrado'
const acceso = useAccesoSolicitud()
const route = useRoute()
const avisoCerrado = ref(true) // hasta montar en el navegador no se sabe si lo cerró: así no aparece y desaparece
onMounted(() => {
  try { avisoCerrado.value = sessionStorage.getItem(CLAVE_AVISO_CERRADO) === '1' } catch { avisoCerrado.value = false }
})
function cerrarAviso() {
  avisoCerrado.value = true
  try { sessionStorage.setItem(CLAVE_AVISO_CERRADO, '1') } catch {}
}
const mostrarAviso = computed(() => acceso.avisoBarra.value && !avisoCerrado.value && !PAGINAS_ENCARGO.test(route.path))
const textoAviso = computed(() => acceso.textoAvisoBarra.value || t('Prepare su trámite online'))
const estiloAviso = computed(() => {
  const letra = NOTARIA.value.Theme.DatosContacto.Letra
  return { color: letra.Color, fontFamily: letra.Font, fontSize: letra.Size, fontWeight: 600, textDecoration: 'underline' }
})
</script>
<style lang="css" scoped>
.v-app-bar-fixed-mobile .stickybar-text{
  min-height: 32px !important;
  height: auto !important;
  padding: 4px 0 !important;
  z-index: 99999;
  font-size: calc(0.7em + 0.5vw) !important;
  line-height: normal !important;
  vertical-align: middle;
}
.aviso-encargo {
  flex: 0 1 auto;
  min-width: 0;
}
.aviso-cerrar {
  display: inline-flex;
  align-items: center;
  padding: 2px;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
}
.aviso-cerrar:hover {
  background: rgba(127, 127, 127, 0.2);
}
.stickybar-text{
  /* text-overflow: ellipsis;
  overflow: hidden; */
}
</style>